import PopoverItems from '@/components/PopoverItems/PopoverItems';
import RegularText from '@/components/RegularText';
import { config } from '@/config';
import { AvailabilityType } from '@/constants/enum';
import { arrayToStringByComma } from '@/helpers';
import { useGetProductsAndCollectionsQuery } from '@/redux/query';
import {
  shippingCollectionSelector,
  shippingDisableSelector,
  shippingProductSelector,
  shippingSettingSelector,
  shippingStateSelector,
} from '@/redux/slice/shipping.slice';
import { IShippingOverrideDateRanges } from '@/types/components/shipping';
import {
  ActionList,
  BlockStack,
  Box,
  Button,
  FormLayout,
  InlineError,
  InlineGrid,
  InlineStack,
  Popover,
  TextField,
} from '@shopify/polaris';
import { DatabaseAddIcon, DeleteIcon, ProductAddIcon } from '@shopify/polaris-icons';
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

const OverrideTimeBlock = ({
  dateRangeRules,
  productsIdChosen,
  collectionsIdChosen,
  onRemove,
  onSelectProducts,
  onSelectCollections,
  onChangePreparationDay,
  onChangeDateRange,
}: {
  dateRangeRules: IShippingOverrideDateRanges;
  productsIdChosen: string[];
  collectionsIdChosen: string[];
  onRemove: () => void;
  onSelectProducts: (filteredProducts: string[]) => void;
  onSelectCollections: (filteredCollections: string[]) => void;
  onChangePreparationDay: (value: string) => void;
  onChangeDateRange: (value: string) => void;
}) => {
  const shippingSettings = useSelector(shippingSettingSelector);
  const [actionListActive, setActionListActive] = useState<boolean>(false);
  const toggleActive = useCallback(() => setActionListActive((state) => !state), []);
  const shippingProducts = useSelector(shippingProductSelector);
  const shippingCollections = useSelector(shippingCollectionSelector);
  const productsIdLeft = shippingProducts.filter(
    (id) => !productsIdChosen.includes(id) || dateRangeRules.productIds.includes(id),
  );
  const collectionsIdLeft = shippingCollections.filter(
    (id) => !collectionsIdChosen.includes(id) || dateRangeRules.collectionIds.includes(id),
  );
  const { data: productsAndCollectionsData } = useGetProductsAndCollectionsQuery({
    shop: config.shop,
    productIds: arrayToStringByComma(dateRangeRules.productIds),
    collectionIds: arrayToStringByComma(dateRangeRules.collectionIds),
  });
  const shippingState = useSelector(shippingStateSelector);
  const shippingDisable = useSelector(shippingDisableSelector);

  const blockProducts = useMemo(
    () =>
      productsAndCollectionsData?.data.products.map((item) => ({
        title: item?.title,
        imgSrc: item?.featuredMedia?.preview?.image?.url,
      })),
    [productsAndCollectionsData?.data.products],
  );

  const blockCollections = useMemo(
    () => productsAndCollectionsData?.data.collections.map((item) => ({ title: item?.title, imgSrc: item?.image?.url })),
    [productsAndCollectionsData?.data.collections],
  );

  const activator = (
    <Button onClick={toggleActive} disabled={shippingDisable.isDisableProductOverride} disclosure>
      Actions
    </Button>
  );

  const header = useMemo(() => {
    if (dateRangeRules.productIds.length && dateRangeRules.collectionIds.length) {
      return (
        <InlineStack gap={'100'} blockAlign="end">
          <RegularText>Selected</RegularText>
          <PopoverItems
            title={`${dateRangeRules.productIds?.length} product${dateRangeRules.productIds?.length > 1 ? 's' : ''}`}
            items={blockProducts || []}
          />
          <RegularText>and</RegularText>
          <PopoverItems
            title={`${dateRangeRules.collectionIds?.length} collection${dateRangeRules.collectionIds?.length > 1 ? 's' : ''}`}
            items={blockCollections || []}
          />
        </InlineStack>
      );
    } else if (dateRangeRules.productIds.length) {
      return (
        <PopoverItems
          title={`${dateRangeRules.productIds.length} product${dateRangeRules.productIds.length > 1 ? 's' : ''} selected`}
          items={blockProducts || []}
        />
      );
    } else if (dateRangeRules.collectionIds.length) {
      return (
        <PopoverItems
          title={`${dateRangeRules.collectionIds.length} collection${
            dateRangeRules.collectionIds.length > 1 ? 's' : ''
          } selected`}
          items={blockCollections || []}
        />
      );
    }
    return 'No products or collections selected';
  }, [blockCollections, blockProducts, dateRangeRules.collectionIds, dateRangeRules.productIds]);

  return (
    <Box paddingInlineStart="200" borderInlineStartWidth="050">
      <BlockStack gap="200">
        <InlineGrid columns="1fr auto">
          <RegularText>{header}</RegularText>
          <Popover
            active={actionListActive}
            activator={activator}
            autofocusTarget="first-node"
            onClose={() => setActionListActive(false)}
          >
            <ActionList
              actionRole="menuitem"
              sections={[
                {
                  items: [
                    {
                      content: 'Select product',
                      onAction: () => {
                        toggleActive();
                        onSelectProducts(productsIdLeft);
                      },
                      disabled:
                        shippingSettings.productAvailabilityType !== AvailabilityType.ALL &&
                        (!shippingProducts.length || !productsIdLeft.length),
                      icon: ProductAddIcon,
                    },
                    {
                      content: 'Select collection',
                      onAction: () => {
                        toggleActive();
                        onSelectCollections(collectionsIdLeft);
                      },
                      disabled:
                        shippingSettings.productAvailabilityType !== AvailabilityType.ALL &&
                        (!shippingCollections.length || !collectionsIdLeft.length),
                      icon: DatabaseAddIcon,
                    },
                    {
                      destructive: true,
                      content: 'Remove',
                      onAction: () => {
                        toggleActive();
                        onRemove();
                      },
                      icon: DeleteIcon,
                    },
                  ],
                },
              ]}
            />
          </Popover>
        </InlineGrid>
        <FormLayout>
          <FormLayout.Group>
            <TextField
              type="number"
              label="No. of preparation day (days) "
              min={0}
              value={dateRangeRules.overridePreparationDay.toString()}
              onChange={(value) => onChangePreparationDay(value)}
              autoComplete="off"
              disabled={shippingDisable.isDisableProductOverride}
            />
            <TextField
              type="number"
              label="Available shipping range (days)"
              min={0}
              value={dateRangeRules.overrideDeliveryDay.toString()}
              onChange={(value) => onChangeDateRange(value)}
              autoComplete="off"
              disabled={shippingDisable.isDisableProductOverride}
            />
          </FormLayout.Group>
        </FormLayout>
        {shippingState.isSaving && !dateRangeRules.productIds.length && !dateRangeRules.collectionIds.length && (
          <InlineError message={'Please select at least one product or collection'} fieldID="" />
        )}
      </BlockStack>
    </Box>
  );
};

export default OverrideTimeBlock;
